import yaml from 'js-yaml';
import { API_CONFIG } from '../utils/constants';

export interface VibeTriggerEvent {
  gif: string;
  category: string;
  duration: number;
}

interface VibeConfigSettings {
  defaultDuration: number;
  giphyRating: string;
  giphyLimit: number;
  enableGiphy: boolean;
}

interface VibeCategoryConfig {
  searchTerm: string;
  duration: number;
  fallbackGif: string;
}

interface VibeConfig {
  settings: VibeConfigSettings;
  categories: Record<string, VibeCategoryConfig>;
}

export const VIBE_TRIGGER_EVENT = 'vibe-trigger';
export const VIBE_DISMISS_EVENT = 'vibe-dismiss';
export const VIBE_VISIBILITY_EVENT = 'vibe-visibility-change';

const DEFAULT_CONFIG: VibeConfig = {
  settings: {
    defaultDuration: 6000,
    giphyRating: 'pg',
    giphyLimit: 25,
    enableGiphy: true,
  },
  categories: {
    bored: {
      searchTerm: 'bored cat',
      duration: 6000,
      fallbackGif: '/assets/gif_bored.gif',
    },
  },
};

class VibeService {
  private config: VibeConfig | null = null;
  private configPromise: Promise<VibeConfig> | null = null;
  private triggerGeneration = 0;

  private async loadConfig(): Promise<VibeConfig> {
    if (this.config) {
      return this.config;
    }

    if (this.configPromise) {
      return this.configPromise;
    }

    this.configPromise = (async () => {
      try {
        const response = await fetch('/vibe-config.yaml');
        if (!response.ok) {
          console.warn('[VibeService] Failed to load vibe-config.yaml, using defaults');
          this.config = DEFAULT_CONFIG;
          return this.config;
        }

        const text = await response.text();
        const parsed = yaml.load(text) as VibeConfig;
        
        if (!parsed || !parsed.settings || !parsed.categories) {
          console.warn('[VibeService] Invalid config format, using defaults');
          this.config = DEFAULT_CONFIG;
          return this.config;
        }

        this.config = parsed;
        return this.config;
      } catch (error) {
        console.warn('[VibeService] Error loading config:', error);
        this.config = DEFAULT_CONFIG;
        return this.config;
      }
    })();

    return this.configPromise;
  }

  private async getCategory(categoryName: string): Promise<VibeCategoryConfig> {
    const config = await this.loadConfig();
    return config.categories[categoryName] ?? {
      searchTerm: categoryName,
      duration: config.settings.defaultDuration,
      fallbackGif: '/assets/gif_bored.gif',
    };
  }

  private async getSettings(): Promise<VibeConfigSettings> {
    const config = await this.loadConfig();
    return config.settings;
  }

  private async fetchGiphyGif(searchTerm: string): Promise<string | null> {
    const settings = await this.getSettings();
    
    if (!settings.enableGiphy) {
      console.log('[VibeService] Giphy disabled in config');
      return null;
    }

    try {
      console.log('[VibeService] Fetching from backend:', searchTerm);
      const params = new URLSearchParams({
        q: searchTerm,
        limit: String(settings.giphyLimit),
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}giphy/search?${params}`);
      console.log('[VibeService] Backend response status:', response.status);
      
      if (!response.ok) {
        console.warn('[VibeService] Backend request failed:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('[VibeService] Backend response:', data);
      
      // Use preview_url (smaller, faster) with fallback to full url
      const gifUrl = data.preview_url ?? data.url ?? null;
      console.log('[VibeService] Selected GIF:', gifUrl ? 'Success' : 'Failed');
      return gifUrl;
    } catch (error) {
      console.error('[VibeService] Backend fetch error:', error);
      return null;
    }
  }

  async fetchGif(categoryName: string): Promise<string> {
    const category = await this.getCategory(categoryName);
    const giphyUrl = await this.fetchGiphyGif(category.searchTerm);
    if (giphyUrl) {
      return giphyUrl;
    }
    return category.fallbackGif;
  }

  async trigger(categoryName: string, customDuration?: number): Promise<void> {
    // Opt-in: vibe gifs run only when explicitly enabled in Settings / localStorage
    const vibeGifsEnabled = localStorage.getItem('vibe_gifs_enabled') === 'true';
    if (!vibeGifsEnabled) {
      return;
    }

    const generation = ++this.triggerGeneration;
    const category = await this.getCategory(categoryName);
    const duration = customDuration ?? category.duration;

    const eventDetail: VibeTriggerEvent = {
      gif: category.fallbackGif,
      category: categoryName,
      duration,
    };

    window.dispatchEvent(
      new CustomEvent(VIBE_TRIGGER_EVENT, { detail: eventDetail })
    );

    const giphyUrl = await this.fetchGiphyGif(category.searchTerm);
    if (giphyUrl && this.triggerGeneration === generation) {
      window.dispatchEvent(
        new CustomEvent(VIBE_TRIGGER_EVENT, {
          detail: { ...eventDetail, gif: giphyUrl },
        })
      );
    }
  }

  show(): void {
    window.dispatchEvent(
      new CustomEvent(VIBE_VISIBILITY_EVENT, { detail: { visible: true } })
    );
  }

  dismiss(): void {
    this.triggerGeneration++;
    window.dispatchEvent(new CustomEvent(VIBE_DISMISS_EVENT));
  }

  hide(): void {
    window.dispatchEvent(
      new CustomEvent(VIBE_VISIBILITY_EVENT, { detail: { visible: false } })
    );
  }

  async getCategories(): Promise<string[]> {
    const config = await this.loadConfig();
    return Object.keys(config.categories);
  }

  async reloadConfig(): Promise<void> {
    this.config = null;
    this.configPromise = null;
    await this.loadConfig();
  }
}

export const vibeService = new VibeService();
export default vibeService;
